import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useRef, useState } from 'react';
import { freebeIntegrationApi, type FreebeMfaChallengeResult } from './freebe-integration-api';

export function FreebeMfaDialog({
  challenge,
  csrfToken,
  onClose,
}: {
  readonly challenge: FreebeMfaChallengeResult;
  readonly csrfToken: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const waiting = challenge.status === 'WAITING';
  return (
    <ModalDialog
      ref={dialog}
      className="remuneration-goal-overlay"
      aria-labelledby="freebe-mfa-title"
      onCancel={onClose}
      close={
        <ModalCloseButton
          className="remuneration-goal-overlay__close"
          aria-label="Fermer"
          onClick={onClose}
        />
      }
    >
      <form
        className="remuneration-goal"
        onSubmit={(event) => {
          event.preventDefault();
          if (sending || !waiting || !/^\d{6}$/u.test(code)) return;
          const submittedCode = code;
          setCode('');
          setSending(true);
          setError(null);
          void freebeIntegrationApi
            .submitMfa(challenge.id, submittedCode, csrfToken)
            .catch(() =>
              setError(
                'Le code n’a pas pu être transmis. Vérifiez que la demande est toujours active.',
              ),
            )
            .finally(() => setSending(false));
        }}
      >
        <header className="tosho-modal-heading tosho-modal-header">
          <h2 id="freebe-mfa-title">Freebe attend votre code MFA</h2>
        </header>
        <p>
          La synchronisation est en pause. Saisissez le code demandé par Freebe depuis votre
          application d’authentification.
        </p>
        {challenge.attemptCount > 0 && waiting ? (
          <p role="status" className="tosho-feedback is-error">
            Freebe n’a pas accepté le dernier essai. Vous pouvez saisir un nouveau code.
          </p>
        ) : null}
        <label>
          Code à six chiffres
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            autoFocus
            value={code}
            disabled={sending || !waiting}
            onChange={(event) => setCode(event.currentTarget.value.replace(/\D/gu, '').slice(0, 6))}
          />
        </label>
        {error ? (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <p role="status">
          {sending || !waiting
            ? 'Vérification par Freebe…'
            : 'Le code ne sera pas enregistré dans Tosho.'}
        </p>
        <button type="submit" disabled={sending || !waiting || code.length !== 6}>
          Transmettre à Freebe
        </button>
      </form>
    </ModalDialog>
  );
}
